import React, { useState, useRef, useEffect } from 'react'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'
import loadingIcon from 'assets/images/gif/sync-ani.gif'
import attendeesIcon from 'assets/images/calendar/property-attendees.svg'
import inviteIcon from 'assets/images/calendar/invite-24.svg'
import { selectUser } from 'popups'
import classNames from 'classnames'
import * as calendarService from 'pages/calendar/modules/CalendarService'
// 순환 참조 방지를 위해 EventManager 대신 공유 유틸리티에서 import
import { isPersonaEmail, getDisplayEmailFromPersonaEmail } from 'utils/personaEmail'
import { isOrgSharedAccountEmail, getOuCodeFromEmail } from 'utils/orgSharedAccount'
import ouCalendarService from 'services/ouCalendar'
import useOrgSharedOwnerName from '../../hooks/useOrgSharedOwnerName'
import FolderUserListCell from '../FolderEditor/FolderUserListCell'
import { ACCEPTED, DECLINED, NO_RESPONSE, OWNER } from '../common/UserListCell'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'
import settings from 'popups/Settings'

interface Folder {
  url: string
  displayName: string
  isShared: boolean
  isReadOnly?: boolean
  owner: string
  calendarColor?: { _cdata: string }
}
interface Invitee {
  email: string
  name: string
  status: number
  isReadOnly: boolean
}
interface FolderShareInfoProps {
  folder: Folder
  onClose: () => void
}
const FolderShareInfo: React.FC<FolderShareInfoProps> = ({ folder, onClose }) => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [invitees, setInvitees] = useState<Invitee[]>([])
  const firstLoad = useRef<boolean>(false)
  const ownerOuName = useOrgSharedOwnerName(folder?.owner)

  const loadInvitees = async (): Promise<void> => {
    setIsLoading(true)
    calendarService.getCalendarInfo(folder.url, async (result: any) => {
      console.log('get calendar info result %o', result)

      const user = result?.props?.invite?.user
      if (!user) {
        setInvitees([])
        setIsLoading(false)
        return
      }
      let theInvitees: Invitee[] = []
      let users = user
      if (!Array.isArray(user)) {
        users = [user]
      }

      for (let i = 0; i < users.length; i++) {
        const user = users[i]
        let email = user.href.toLowerCase().startsWith('mailto:') ? user.href.slice(7) : user.href
        const status = user.inviteAccepted ? ACCEPTED : user.inviteDeclined ? DECLINED : NO_RESPONSE

        if (isPersonaEmail(email)) {
          email = getDisplayEmailFromPersonaEmail(email)
        }

        const contact = await orgChartHelper.findContact(email)
        let name: string
        if (isOrgSharedAccountEmail(email)) {
          await ouCalendarService.load()
          const ouCode = getOuCodeFromEmail(email)
          name =
            (ouCode && ouCalendarService.getOuName(ouCode)) || t('calendar.share.orgSharedAccount')
        } else {
          name = contact?.name || email.split('@')[0]
        }

        const userInfo = {
          email: email,
          name: name,
          status: status,
          isReadOnly: user.access.read,
        }
        theInvitees.push(userInfo)
      }

      const locale = settings.getValue('general').locale
      theInvitees = theInvitees.sort((a, b) => a.name.localeCompare(b.name, locale))

      setInvitees(theInvitees)
      setIsLoading(false)
    })
  }

  useEffect(() => {
    if (!firstLoad.current) {
      firstLoad.current = true
      if (folder && !folder.isShared) {
        loadInvitees()
      }
    }
  }, [folder])

  const openSelectUser = (options: any) => {
    const { title } = options

    const addedList = invitees.map((invitee) => invitee.email)
    const userInfo = calendarService.getUserInfo()
    const prevEmailList = [...addedList, userInfo.email]

    selectUser({
      userOnly: true,
      title: title || '',
      defaultEmailList: prevEmailList,
      excludeEmailList: [],
      disableEmails: prevEmailList,
      appendUserTitles: { [userInfo.email]: t('settings.calendarTabs.owner') } as any,
      onOk: (data: any, closeFunc: any) => {
        console.log(' -- selected user : ', data)
        const { selectedUserList } = data

        const userInfoList = selectedUserList
          .filter((item) => {
            return item.email !== userInfo.email
          })
          .map((item) => {
            const { name, email } = item
            return { name, email }
          })

        let requestCount = userInfoList.length
        userInfoList.forEach((userInfo) => {
          setIsLoading(true)
          calendarService.sendInviteCalendar(
            folder.url,
            folder.displayName,
            userInfo.email,
            false,
            (_success) => {
              requestCount--
              if (requestCount === 0) {
                loadInvitees()
              } else {
                setIsLoading(false)
              }
            },
          )
        })

        closeFunc()
      },
    })
  }

  return (
    <div className={classNames(styles.container, { [styles.shared]: folder?.isShared })}>
      <div className={styles.header}>
        <h2>{folder?.displayName || t('calendar.share.title')}</h2>
      </div>
      {folder && folder?.isShared && (
        <React.Fragment>
          <div className={styles.sharedLabelWith}>{t('calendar.share.sharedWithYou')}</div>
          <div className={styles.permission}>
            {t('settings.calendarTabs.permission')} :{' '}
            {folder.isReadOnly
              ? t('settings.calendarTabs.viewOnly')
              : t('settings.calendarTabs.viewAndEdit')}
          </div>

          <div className={styles.sharedInfoUserCell}>
            <FolderUserListCell
              key={folder.owner}
              showMore={false}
              onChangePermission={() => {}}
              onStopSharing={() => {}}
              email={folder.owner}
              name={
                isOrgSharedAccountEmail(folder.owner)
                  ? (ownerOuName ?? t('calendar.share.orgSharedAccount'))
                  : undefined
              }
              nameSuffix={
                <div style={{ marginLeft: '4px', color: folder.calendarColor._cdata }}>
                  {t('settings.calendarTabs.owner')}
                </div>
              }
              status={OWNER}
            />
          </div>
          <div style={{ height: '16px' }}></div>
        </React.Fragment>
      )}
      {folder && !folder.isShared && (
        <>
          <div className={styles.sharedLabel}>
            {t('calendar.edit.sharedByYou')}
            <button className={styles.inviteButton}>
              <img
                src={inviteIcon}
                alt="Invite"
                onClick={() =>
                  openSelectUser({ title: t('calendar.edit.invitation'), key: 'invitation' })
                }
              />
            </button>
          </div>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <img src={loadingIcon} alt="Loading" />
            </div>
          ) : invitees.length > 0 ? (
            <div className={styles.invitees}>
              {invitees.map((invitee) => (
                <FolderUserListCell
                  key={invitee.email}
                  name={invitee.name}
                  email={invitee.email}
                  status={invitee.status}
                  isReadOnly={invitee.isReadOnly}
                  showMore={true}
                  onChangePermission={(isReadOnly) => {
                    // 권한 변경 처리
                    console.log('onChangePermission %o', isReadOnly)
                    setIsLoading(true)
                    calendarService.sendInviteCalendar(
                      folder.url,
                      folder.displayName,
                      invitee.email,
                      isReadOnly,
                      (success) => {
                        if (success) {
                          loadInvitees()
                        } else {
                          setIsLoading(false)
                        }
                      },
                    )
                  }}
                  onStopSharing={() => {
                    // 공유 중지 처리
                    console.log('onStopSharing')
                    setIsLoading(true)
                    calendarService.deleteInviteCalendar(folder.url, invitee.email, (success) => {
                      if (success) {
                        loadInvitees()
                      } else {
                        setIsLoading(false)
                      }
                    })
                  }}
                />
              ))}
            </div>
          ) : (
            <div className={styles.noInvitees}>
              <img src={attendeesIcon} alt="No users" />
              {t('calendar.edit.NoSharedUser')}
            </div>
          )}
        </>
      )}
      <div className={styles.footer}>
        <button className={styles.closeButton} onClick={onClose}>
          {t('common.button.close')}
        </button>
      </div>
    </div>
  )
}

export default FolderShareInfo
